import { Link } from "@/lib/nav";
import { useRouter } from "@/lib/nav";

import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import StatusBadge from "@buildpy/ui/status-badge";
import ListRowCard from "@/components/list-card";
import Money from "@/components/money";
import ResponsiveList from "@/components/responsive-list";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@buildpy/ui/table";
import { EMPTY_MARK, formatDate, formatWon, maskPhone } from "@buildpy/core/lib/format";
import type { HeadStage } from "@buildpy/core/modules/customer/journey-panel";
import { CustomerStage } from "@buildpy/core/modules/customer/types";
import QuoteStatusBadge from "@/modules/quote/ui/status-badge";
import StageBadge from "@/modules/customer/ui/stage-badge";
import { tabHref } from "@/modules/org/ui/settings-tabs";
import { QuoteStatus } from "@buildpy/core/modules/quote/types";

export interface CustomerRow {
  id: string;
  name: string;
  phone: string;
  site_address: string;
  /**
   * 평형대 표기. 코드가 아니라 **이미 풀린 말**이 온다 — 회사 코드값이라
   * 목록 화면이 코드표를 들고 있을 수 없고, 사용 안 함이 된 옛 코드도 표기는 남는다.
   */
  area_band_label: string;
  /** 유입 표기. 내장 고정값이라 코드표를 거치지 않는다. */
  source_label: string;
  stage: CustomerStage;
  // 이탈 뒤 재문의면 보드와 같은 표시 — 없으면 저장 단계 뱃지.
  display?: HeadStage | null;
  assignee_name: string | null;
  /** 가장 나중 견적 한 건. 없으면 null. */
  latest_quote: {
    quote_no: string;
    version: number;
    status: QuoteStatus;
    total: number;
  } | null;
  created_at: string;
}

// 단계 칸 — 재문의면 상세 머리와 같은 「단계 + 재문의」.
const RowStage = ({ row }: { row: CustomerRow }) =>
  row.display ? (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="customer-row-restarted">
      <StatusBadge label={row.display.label} tone={row.display.tone} />
      <Pill>{row.display.note}</Pill>
    </span>
  ) : (
    <StageBadge stage={row.stage} />
  );

interface Props {
  rows: CustomerRow[];
  // 조건 없이 고객이 0명 — 필터로 0건일 때와 안내가 다르다.
  noCustomers?: boolean;
}

/**
 * 고객 목록 표.
 *
 * 행 아무 데나 눌러 상세로 간다. 이름 칸에는 진짜 링크를 둔다 — 행 클릭만
 * 두면 키보드로는 못 열고, 새 탭으로도 못 연다.
 */
const CustomerTable = ({ rows, noCustomers = false }: Props) => {
  const router = useRouter();

  if (rows.length === 0 && noCustomers) {
    return (
      <EmptyState as="div" className="flex flex-col items-center gap-y-1">
        <p className="font-medium text-foreground">아직 고객이 없습니다.</p>
        <p className="text-xs" data-testid="customer-empty-intake">
          간편접수 링크·QR 을 홈페이지·명함에 붙이면 문의가 여기로 들어옵니다.{" "}
          <Link className="font-medium text-primary hover:underline" href={tabHref("intake")}>
            간편접수 링크 보기
          </Link>
        </p>
      </EmptyState>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState as="div" className="flex flex-col items-center gap-y-1">
        <p className="font-medium text-foreground">조건에 맞는 고객이 없습니다.</p>
        <p className="text-xs">검색어나 단계 필터를 지워 보세요.</p>
      </EmptyState>
    );
  }

  // 폰(md 미만)은 한 행 = 카드 한 장 — 표는 단계·최근 견적 열이 화면 밖으로 밀렸다.
  // 메타는 항목이 많아 금액 옆에서 말줄임되던 줄 — 다음 줄로 꺾고, 날짜는 맨 앞에 둔다.
  const cards = rows.map((row) => (
    <ListRowCard
      key={row.id}
      amount={row.latest_quote ? `견적 ${formatWon(row.latest_quote.total)}` : undefined}
      badge={<RowStage row={row} />}
      href={`/app/customers/${row.id}`}
      meta={[formatDate(row.created_at), maskPhone(row.phone, ""), row.assignee_name, row.source_label]}
      metaWrap
      subtitle={[row.site_address, row.area_band_label].filter(Boolean).join(" · ") || undefined}
      testId={`customer-card-${row.id}`}
      title={row.name}
    />
  ));

  return (
    <ResponsiveList
      cards={cards}
      label="고객 목록"
      testId="customer-cards"
      table={
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="h-10 min-w-24 whitespace-nowrap px-2 xl:px-3">이름</TableHead>
                <TableHead className="h-10 px-2 xl:px-3">전화</TableHead>
                <TableHead className="h-10 px-2 xl:px-3">현장 · 평형</TableHead>
                <TableHead className="h-10 px-2 xl:px-3">유입</TableHead>
                <TableHead className="h-10 px-2 xl:px-3">단계 · 담당</TableHead>
                <TableHead className="h-10 px-2 xl:px-3">최근 견적</TableHead>
                <TableHead className="h-10 whitespace-nowrap px-2 text-right xl:px-3">등록일</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => router.push(`/app/customers/${row.id}`)}>
                  <TableCell className="min-w-24 max-w-48 truncate px-2 py-2 font-medium xl:px-3" title={row.name}>
                    <Link
                      className="hover:underline"
                      href={`/app/customers/${row.id}`}
                      onClick={(event) => event.stopPropagation()}>
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2 tabular-nums text-muted-foreground xl:px-3">
                    {maskPhone(row.phone)}
                  </TableCell>
                  <TableCell className="max-w-64 px-2 py-2 text-muted-foreground xl:px-3">
                    {/* 표 칸 max-w 는 자동 배치에서 안 먹어 글자 폭이 열을 민다 — 줄 쪽에 상한을 둔다 */}
                    <span className="block max-w-48 truncate xl:max-w-64" title={row.site_address || undefined}>
                      {row.site_address || EMPTY_MARK}
                    </span>
                    {row.area_band_label && <span className="block text-xs">{row.area_band_label}</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2 text-muted-foreground xl:px-3">
                    {row.source_label || EMPTY_MARK}
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2 xl:px-3">
                    <RowStage row={row} />
                    {row.assignee_name && (
                      <span className="block pt-0.5 text-xs text-muted-foreground">{row.assignee_name}</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2 xl:px-3">
                    {row.latest_quote ? (
                      <span className="flex items-center gap-x-2">
                        <QuoteStatusBadge status={row.latest_quote.status} />
                        <Money className="text-muted-foreground" value={row.latest_quote.total} />
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{EMPTY_MARK}</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-muted-foreground xl:px-3">
                    {formatDate(row.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      }
    />
  );
};

export default CustomerTable;
